// Item 406 — a group's level rules on the dashboard (design §4.3). The server validates and decides
// (services/level_rules.py, the handler's byte-identical twin); here only what the rules sheet needs to show a row and
// to send it back whole: the defaults, and the same "laid over the defaults, unknown keys kept" reading. Pure, no React.
import type { AppearanceClueCode, ClueWeight, LevelRules, LockedClueCode, ScoringClueCode } from '@/types'

/** The clues a rule can weigh in v1. `person` (the gate) and `spray_sound` (the override) are never weighed. */
export const SCORING_CLUES: readonly ScoringClueCode[] = ['still', 'staying', 'at_train']
/** #408 — judged per person by the captioner, only on a camera with the Beta switch on (rules.py APPEARANCE_CLUES). */
export const APPEARANCE_CLUES: readonly AppearanceClueCode[] = ['no_hivis', 'backpack', 'hoodie', 'mask', 'spray_can']
/** The clues registered for later items: they always answer not assessed, so the sheet shows them locked. */
export const LOCKED_CLUES: readonly LockedClueCode[] = ['running']
export const CLUE_WEIGHTS: readonly ClueWeight[] = ['off', 'weak', 'strong']

/** Design §4.3, verbatim. */
export const DEFAULT_LEVEL_RULES: LevelRules = {
  version: 1,
  suspicious_enabled: false,
  clues: { still: 'weak', staying: 'weak', at_train: 'weak', no_hivis: 'off', backpack: 'off', hoodie: 'off', mask: 'off', spray_can: 'off', running: 'off' },
  bars: { suspicious: 1, critical: 3 },
  person_derived_cap: 2,
  params: {
    still: { min_seconds: 10, max_body_lengths_per_s: 0.2 },
    staying: { min_seconds: 20, min_uploads: 2, window_minutes: 15 },
    at_train: { min_frames: 3 },
  },
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

function merge(base: Record<string, unknown>, over: Record<string, unknown>) {
  for (const [key, value] of Object.entries(over)) {
    const inBase = base[key]
    if (isObject(value) && isObject(inBase)) merge(inBase, value)
    else base[key] = value
  }
}

/** The stored rules over the defaults (rules.py `normalise`): every missing key filled, every unknown key kept (a later
 *  item's clue is not lost on save), the version forced. A new object; anything that is not an object reads as the defaults. */
export function normaliseLevelRules(rules: unknown): LevelRules {
  const out = structuredClone(DEFAULT_LEVEL_RULES) as unknown as Record<string, unknown>
  if (isObject(rules)) merge(out, structuredClone(rules))
  out.version = DEFAULT_LEVEL_RULES.version
  return out as unknown as LevelRules
}

/** #408 — the owner's rules for the client's test, laid over `rules`: Suspicious on; the five appearance clues and
 *  standing still weak; staying and the train zone off; 1 point = Suspicious, 2 = Critical; at most 1 point from clues
 *  about the person; standing still from 4 s (the clips run about 5 s). A new object; everything else is kept. */
export function withBetaTestRules(rules: LevelRules): LevelRules {
  const out = structuredClone(rules)
  out.suspicious_enabled = true
  for (const code of APPEARANCE_CLUES) out.clues[code] = 'weak'
  out.clues.still = 'weak'
  out.clues.staying = 'off'
  out.clues.at_train = 'off'
  out.bars = { suspicious: 1, critical: 2 }
  out.person_derived_cap = 1
  out.params.still.min_seconds = 4
  return out
}

/** One number box of the sheet's Advanced fold: where it lives in the rules, and the range rules.py checks. */
export interface LevelRuleField {
  key: 'barSuspicious' | 'barCritical' | 'personCap' | 'stillSeconds' | 'stillSpeed' | 'stayingSeconds' | 'stayingUploads' | 'stayingMinutes' | 'atTrainFrames'
  path: readonly string[]
  min: number
  max: number
  step: number
}

export const LEVEL_RULE_FIELDS: readonly LevelRuleField[] = [
  { key: 'barSuspicious', path: ['bars', 'suspicious'], min: 1, max: 6, step: 1 },
  { key: 'barCritical', path: ['bars', 'critical'], min: 1, max: 6, step: 1 },
  { key: 'personCap', path: ['person_derived_cap'], min: 0, max: 6, step: 1 },
  { key: 'stillSeconds', path: ['params', 'still', 'min_seconds'], min: 1, max: 600, step: 1 },
  { key: 'stillSpeed', path: ['params', 'still', 'max_body_lengths_per_s'], min: 0.01, max: 5, step: 0.01 },
  { key: 'stayingSeconds', path: ['params', 'staying', 'min_seconds'], min: 1, max: 600, step: 1 },
  { key: 'stayingUploads', path: ['params', 'staying', 'min_uploads'], min: 1, max: 20, step: 1 },
  { key: 'stayingMinutes', path: ['params', 'staying', 'window_minutes'], min: 1, max: 1440, step: 1 },
  { key: 'atTrainFrames', path: ['params', 'at_train', 'min_frames'], min: 1, max: 100, step: 1 },
]

export function readRuleField(rules: LevelRules, path: readonly string[]): unknown {
  let node: unknown = rules
  for (const key of path) node = isObject(node) ? node[key] : undefined
  return node
}

/** A copy of `rules` with one number replaced. */
export function withRuleField(rules: LevelRules, path: readonly string[], value: number): LevelRules {
  const out = structuredClone(rules) as unknown as Record<string, unknown>
  let node = out
  for (const key of path.slice(0, -1)) node = node[key] as Record<string, unknown>
  node[path[path.length - 1]] = value
  return out as unknown as LevelRules
}

/** What a number box holds as a number; null = not a number (empty, or half typed). The range is the server's to refuse. */
export function parseRuleNumber(text: string): number | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}
